// price-working-edit — deterministic edits of one stored Price Working line.
// (The Stage 4 re-cost through cost-math, applyPriceWorkingLineEdit, was retired
// with build-up pricing step 2, 2026-11-03.)
//
// The stored cost stack is an auditable project record. Every constituent row
// therefore keeps its category and position: editing one duplicate must not
// merge it with its sibling or move an Other cost into Equipment. rowIndex is
// the primary identity. The older rowKey form is accepted only when it resolves
// to exactly one row, so legacy callers fail closed instead of collapsing data.
//
// Quantity-driver edits retain the current rate-book authority. A builder edit
// to a rate or line total changes the commercial authority, so stale catalogue
// match stamps are removed and the row is explicitly marked manual.

import {
    hasExplicitPriceWorkingDriver,
    priceWorkingBillableQuantity,
    priceWorkingRowQuantity,
} from '../../backend/src/shared/pricing-core/price-working-quantity.js';
import {
    clearCurrentPricingResolutionState,
    pricingResolutionIdentity,
    pricingRowHasPendingResolution,
} from '../services/pricing-resolution';

// The quantity readers moved to pricing-core (Quote Brain slice PC).
export {
    hasExplicitPriceWorkingDriver,
    isPerDayPriceWorkingRow,
    priceWorkingBillableQuantity,
    priceWorkingDayDriverValue,
    priceWorkingUnit,
} from '../../backend/src/shared/pricing-core/price-working-quantity.js';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const round2 = (v) => Math.round(num(v) * 100) / 100;

const COST_CATEGORIES = new Set(['materials', 'labour', 'equipment', 'subcontractors', 'otherCosts']);

const NUMERIC_FIELDS = {
    materials: new Set(['qty', 'quantity', 'unitRate', 'unit_rate', 'rate', 'total']),
    labour: new Set(['hours', 'workers', 'rate', 'qty', 'quantity', 'duration_days', 'durationDays', 'billableQuantity', 'billable_quantity', 'total']),
    equipment: new Set(['qty', 'quantity', 'rate', 'unitRate', 'unit_rate', 'duration_days', 'durationDays', 'billableQuantity', 'billable_quantity', 'total']),
    subcontractors: new Set(['hours', 'workers', 'qty', 'quantity', 'rate', 'unitRate', 'unit_rate', 'duration_days', 'durationDays', 'billableQuantity', 'billable_quantity', 'total']),
    otherCosts: new Set(['qty', 'quantity', 'rate', 'unitRate', 'unit_rate', 'billableQuantity', 'billable_quantity', 'total']),
};

const MANUAL_AUTHORITY_FIELDS = new Set(['rate', 'unitRate', 'unit_rate', 'total']);
const RATE_BOOK_STAMPS = [
    'pricing_item_id', 'matchedName', 'matched_name', 'matchedUnit', 'matched_unit',
    'matchedRate', 'matched_rate', 'matchedBy', 'matched_by', 'matchConfidence', 'confidence',
    'match_confidence', 'rateSourceRating', 'rate_source_rating', 'rateSourceNote',
    'rate_source_note', 'marketEvidence', 'market_evidence',
    'marketPricingEvidence', 'market_pricing_evidence', 'marketSourceUrl', 'market_source_url',
];

export function hasEditablePriceWorkingLines(costBreakdown) {
    return !!costBreakdown && [...COST_CATEGORIES].some((category) => (
        Array.isArray(costBreakdown[category]) && costBreakdown[category].length > 0
    ));
}

/** Legacy Price Working identity. New callers should pass rowIndex too. */
export function priceWorkingRowKey(row, category) {
    if (!row) return '';
    if (category === 'labour' || category === 'subcontractors') {
        return `${String(row.trade || '').trim().toLowerCase()}|${num(row.rate)}|${num(row.workers) || 1}`;
    }
    if (category === 'equipment') {
        return String(row.description || '').trim().toLowerCase();
    }
    return `${String(row.description || '').trim().toLowerCase()}|${num(row.unitRate ?? row.rate)}|${String(row.unit || '').trim().toLowerCase()}`;
}

export function priceWorkingRateField(row, category) {
    const order = category === 'labour' || category === 'subcontractors'
        ? ['rate', 'unitRate', 'unit_rate']
        : ['unitRate', 'unit_rate', 'rate'];
    const valued = order.find((key) => (
        row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== ''
    ));
    if (valued) return valued;
    return order.find((key) => Object.prototype.hasOwnProperty.call(row || {}, key)) || order[0];
}

function rowRate(row, category) {
    return num(row?.[priceWorkingRateField(row, category)]);
}

function updateStoredBillableQuantity(row, value) {
    if (Object.prototype.hasOwnProperty.call(row, 'billableQuantity')) row.billableQuantity = round2(value);
    if (Object.prototype.hasOwnProperty.call(row, 'billable_quantity')) row.billable_quantity = round2(value);
}

function recomputeRowTotal(row, category, editedField) {
    if (editedField === 'total') return round2(row.total);

    const rate = rowRate(row, category);
    if (category === 'labour' || category === 'subcontractors') {
        const billable = priceWorkingBillableQuantity(row, category);
        updateStoredBillableQuantity(row, billable);
        return round2(billable * rate);
    }

    const quantity = priceWorkingRowQuantity(row);
    if (category === 'equipment') {
        const billable = priceWorkingBillableQuantity(row, category);
        updateStoredBillableQuantity(row, billable);
        return round2(billable * rate);
    }
    return round2(quantity * rate);
}

function markManualAuthority(row, field) {
    const manualFields = new Set(Array.isArray(row.manualOverrideFields) ? row.manualOverrideFields : []);
    manualFields.add(field);
    const sourcePricingItemId = row.sourcePricingItemId || row.source_pricing_item_id
        || row.pricingItemId || row.pricing_item_id;
    const sourceMatchedName = row.sourceMatchedName || row.source_matched_name
        || row.matchedName || row.matched_name;
    for (const key of RATE_BOOK_STAMPS) delete row[key];
    row.pricingItemId = null;
    if (sourcePricingItemId) row.sourcePricingItemId = sourcePricingItemId;
    if (sourceMatchedName) row.sourceMatchedName = sourceMatchedName;
    delete row.source_pricing_item_id;
    delete row.source_matched_name;
    row._rateSource = 'manual';
    row.pricingStatus = 'manual_override';
    row.rateSourceRating = 'manual_override';
    row.rateSourceNote = `Builder overrode ${field} in Stage 4`;
    row.manualOverride = true;
    row.manualOverrideFields = [...manualFields];
}

function sumCategory(rows) {
    return Array.isArray(rows) ? rows.reduce((sum, row) => sum + num(row && row.total), 0) : 0;
}

function sumAllDirect(cb) {
    return sumCategory(cb.materials) + sumCategory(cb.labour) + sumCategory(cb.equipment)
        + sumCategory(cb.subcontractors) + sumCategory(cb.otherCosts);
}

function editItemBreakdownWithResolution(costBreakdown, edit, { allowPricingResolution = true } = {}) {
    const { category, rowIndex, rowKey, field, value } = edit || {};
    if (!costBreakdown || !COST_CATEGORIES.has(category) || !NUMERIC_FIELDS[category]?.has(field)) return null;

    const source = Array.isArray(costBreakdown[category]) ? costBreakdown[category] : [];
    let resolvedIndex = Number.isInteger(rowIndex) && rowIndex >= 0 && rowIndex < source.length
        ? rowIndex
        : null;

    if (resolvedIndex != null && rowKey && priceWorkingRowKey(source[resolvedIndex], category) !== rowKey) {
        return null;
    }
    if (resolvedIndex == null && rowKey) {
        const matches = [];
        source.forEach((row, index) => {
            if (priceWorkingRowKey(row, category) === rowKey) matches.push(index);
        });
        if (matches.length !== 1) return null;
        [resolvedIndex] = matches;
    }
    if (resolvedIndex == null) return null;

    const cb = { ...costBreakdown };
    const rows = source.map((row) => ({ ...row }));
    const row = rows[resolvedIndex];
    const originalResolutionIdentity = pricingResolutionIdentity(row, category, resolvedIndex);
    const pendingBefore = pricingRowHasPendingResolution(row);
    const pendingStatus = row.pricingStatus ?? row.pricing_status;
    const pendingSource = row._rateSource ?? row._rate_source;
    row[field] = Math.max(0, num(value));
    row.total = recomputeRowTotal(row, category, field);
    if (field === 'total') {
        const billableQuantity = priceWorkingBillableQuantity(row, category);
        if (billableQuantity > 0) {
            row[priceWorkingRateField(row, category)] = round2(row.total / billableQuantity);
            updateStoredBillableQuantity(row, billableQuantity);
        }
    }
    delete row._mergedCount;
    const manualAuthorityEdit = MANUAL_AUTHORITY_FIELDS.has(field);
    if (manualAuthorityEdit) markManualAuthority(row, field);
    const positiveTotalOnlyAuthority = field === 'total'
        && row.total > 0
        && !hasExplicitPriceWorkingDriver(row, category);
    const authorityEstablished = allowPricingResolution && manualAuthorityEdit
        && row.total > 0
        && (priceWorkingBillableQuantity(row, category) > 0 || positiveTotalOnlyAuthority);
    if (authorityEstablished) {
        clearCurrentPricingResolutionState(row);
    } else if (pendingBefore) {
        if (pendingStatus != null) row.pricingStatus = pendingStatus;
        if (pendingSource != null) row._rateSource = pendingSource;
    }
    cb[category] = rows;
    cb.directCostTotal = sumAllDirect(cb);
    return {
        costBreakdown: cb,
        resolvedIdentities: authorityEstablished && originalResolutionIdentity
            ? [originalResolutionIdentity]
            : [],
    };
}

/** Apply an inline edit to one exact stored Price Working line. */
export function editItemBreakdown(costBreakdown, edit) {
    return editItemBreakdownWithResolution(costBreakdown, edit)?.costBreakdown || null;
}
